/**
 * KISSED University · Post-submission "Thank You" page.
 * Route: /university/application-submitted
 *
 * Shown immediately after a successful application submission. Displays the
 * immutable public Application ID (KU-YYYY-XXXXXX), thank-you copy, and a
 * "Pay $50 via Square" button that opens the Square static payment link in a
 * new tab so the applicant can keep this confirmation page open for reference.
 *
 * The applicant's `application_id`, `public_application_id`, and `email` are
 * passed via sessionStorage (written by `UniversityApply.jsx` on submit) and
 * also accepted via URL params as a fallback.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Copy, Loader2, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

export default function UniversityApplicationSubmitted() {
  const [params] = useSearchParams();
  const [opts, setOpts] = useState(null);
  const [opening, setOpening] = useState(false);

  // Resolve identifiers — URL params win, otherwise fall back to sessionStorage
  // that `UniversityApply.jsx` wrote at submit time.
  const applicationId = useMemo(() => {
    return (
      params.get("application_id") ||
      sessionStorage.getItem("kissed_uni_last_application_id") ||
      ""
    );
  }, [params]);

  const publicApplicationId = useMemo(() => {
    return (
      params.get("public_application_id") ||
      sessionStorage.getItem("kissed_uni_last_public_application_id") ||
      ""
    );
  }, [params]);

  const email = useMemo(() => {
    return (
      params.get("email") ||
      sessionStorage.getItem("kissed_uni_last_email") ||
      ""
    );
  }, [params]);

  useEffect(() => {
    universityApi.options().then((r) => setOpts(r.data)).catch(() => setOpts(null));
  }, []);

  const fee = opts?.application_fee_usd || 50;
  const reviewWindow = opts?.review_window_days || "3–5 business days";

  const displayId = publicApplicationId || applicationId;

  const copyId = async () => {
    if (!displayId) return;
    try {
      await navigator.clipboard.writeText(displayId);
      toast.success("Application ID copied");
    } catch {
      toast.error("Could not copy. Please copy it manually.");
    }
  };

  const openSquare = async () => {
    if (!applicationId || !email) {
      toast.error("Missing application reference. Please use the Status page.");
      return;
    }
    setOpening(true);
    try {
      const r = await universityApi.startCheckout(applicationId, {
        application_id: applicationId,
        email,
        origin_url: window.location.origin,
      });
      if (r?.data?.already_paid) {
        toast.message("Your payment is already on file.");
        return;
      }
      const url = r?.data?.url;
      if (!url) {
        toast.error("Could not open Square. Please contact admissions.");
        return;
      }
      // Open Square in a new tab so the applicant keeps this confirmation open.
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatApiError(err, "Could not open Square"));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div data-testid="university-application-submitted" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/university">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
          </Link>
          <span className="font-display text-sm text-slate-700">KISSED University · Admissions</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 space-y-6">
        {!displayId ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6" data-testid="submitted-missing-id">
            <AlertCircle className="w-7 h-7 text-red-700" />
            <h2 className="mt-3 font-display text-2xl text-red-900">We can't find your application reference</h2>
            <p className="mt-2 text-sm text-red-800">
              Please check the email we sent you, or visit the status page to look up by Application ID and email.
            </p>
            <div className="mt-4">
              <Button asChild variant="outline">
                <Link to="/university/status">Go to status page</Link>
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Thank-you banner */}
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6" data-testid="submitted-thank-you">
              <CheckCircle2 className="w-8 h-8 text-emerald-700" />
              <h1 className="mt-3 font-display text-3xl text-slate-900">Thank you — your application has been received.</h1>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                Your application to KISSED University of Healing Arts &amp; Sciences™ is saved.
                Please complete your <strong>non-refundable ${fee} application fee</strong> via Square
                to place your application in the admissions review queue. Our team will review
                completed applications within <strong>{reviewWindow}</strong> and email you a decision.
              </p>
            </div>

            {/* Application ID card */}
            <div className="rounded-2xl border border-purple-200 bg-white p-6" data-testid="submitted-id-card">
              <div className="text-xs uppercase tracking-wide text-purple-900 font-semibold">
                Your Application ID
              </div>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <span
                  className="font-mono text-2xl sm:text-3xl text-slate-900 tracking-wider"
                  data-testid="submitted-application-id"
                >
                  {displayId}
                </span>
                <Button variant="outline" size="sm" onClick={copyId} data-testid="submitted-copy-id">
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </div>
              {email ? (
                <div className="mt-3 text-xs text-slate-600">
                  On file: <span className="font-medium">{email}</span>
                </div>
              ) : null}
              <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                Save this ID. You'll use it (with your email) to check your application status at any time.
              </p>
            </div>

            {/* Pay via Square CTA */}
            <div className="rounded-2xl border border-purple-200 bg-purple-50/60 p-6" data-testid="submitted-pay-card">
              <CreditCard className="w-8 h-8 text-purple-900" />
              <h2 className="mt-3 font-display text-xl text-slate-900">Complete your ${fee} application fee</h2>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Payment is processed by <strong>Square</strong>. After you pay, our admissions team manually
                reconciles your transaction and your status flips to <em>Submitted</em>. This typically
                completes within one business day.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button
                  onClick={openSquare}
                  disabled={opening || !applicationId || !email}
                  className="bg-purple-900 hover:bg-purple-800"
                  data-testid="submitted-pay-square-btn"
                >
                  {opening ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-1" />
                  )}
                  Pay ${fee} via Square
                </Button>
                <Button asChild variant="outline" data-testid="submitted-status-link">
                  <Link
                    to={`/university/status?id=${encodeURIComponent(displayId)}${email ? `&email=${encodeURIComponent(email)}` : ""}`}
                  >
                    Check application status <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="text-xs text-slate-500 text-center">
              Questions? Contact <a className="underline" href="mailto:admissions@kissedbycvon.com">admissions@kissedbycvon.com</a>.
            </div>
          </>
        )}
      </main>
    </div>
  );
}
